import { useMemo } from 'react';
import _ from 'lodash';

import { humanize, isoDate } from '@/portainer/filters/filters';
import { trimSHA } from '@/docker/filters/utils';

import { buildNameColumnFromObject } from '@@/datatables/buildNameColumn';

import { Platform, Tag } from '../types';

import { helper } from './helper';
import { actions } from './actions';

// Shown when a tag has no value for a column, e.g. a Helm chart or other
// non-image OCI artifact has no OS/Architecture/Created.
const NOT_APPLICABLE = '-';

const columns = [
  buildNameColumnFromObject<Tag>({
    nameKey: 'Name',
    path: 'portainer.registries.registry.repository.tag',
    dataCy: 'registry-tag-name',
    idParam: 'tag',
    idGetter: (item) => item.Name,
  }),
  helper.accessor((tag) => formatPlatforms(tag.Platforms), {
    id: 'os-architecture',
    header: 'OS/Architecture',
  }),
  helper.accessor((tag) => tag.ImageId ?? '', {
    id: 'image-id',
    header: 'Image ID',
    cell: ({ getValue }) => {
      const imageId = getValue();
      return imageId ? trimSHA(imageId) : NOT_APPLICABLE;
    },
  }),
  helper.display({
    header: 'Compressed Size',
    cell: ({ row }) =>
      row.original.Size !== undefined
        ? humanize(row.original.Size) || NOT_APPLICABLE
        : NOT_APPLICABLE,
  }),
  helper.display({
    header: 'Created',
    cell: ({ row }) =>
      row.original.Created ? isoDate(row.original.Created) : NOT_APPLICABLE,
  }),
];

export function useColumns(advancedFeaturesAvailable: boolean) {
  return useMemo(
    () => _.compact([...columns, advancedFeaturesAvailable && actions]),
    [advancedFeaturesAvailable]
  );
}

function formatPlatforms(platforms: Platform[] | undefined) {
  if (!platforms?.length) {
    return NOT_APPLICABLE;
  }
  return platforms.map(formatPlatform).join(', ');
}

function formatPlatform(platform: Platform) {
  if (!platform.Os && !platform.Architecture) {
    return NOT_APPLICABLE;
  }
  return `${platform.Os || NOT_APPLICABLE}/${platform.Architecture || NOT_APPLICABLE}`;
}
